import { EVENT_TYPES, TypeIdSchema, webhookConversation } from '@unipile/db';
import { Static, Type } from '@sinclair/typebox';
import { createInsertSchema, createSelectSchema } from 'drizzle-typebox';
import { StringEnum } from '../../utils/typebox';

export const WebhookConversationSchema = createSelectSchema(webhookConversation, {
  created_at: Type.String({ description: 'The date and time at which the delivery attempt was made, in ISO 8601 format.' }),
  http_status: Type.Union([Type.Integer(), Type.Null()], {
    description: 'The HTTP status code returned by the endpoint.',
  }),
  first_attempt: Type.Boolean({
    description: 'Whether this delivery was the first attempt to send the webhook. `false` indicates a retry.',
  }),
  event_type: StringEnum([...EVENT_TYPES], { description: 'The type of the event that was delivered.' }),
  endpoint_url: Type.String({ description: 'The URL the webhook was delivered to.' }),
  response_body: Type.Union([Type.String(), Type.Null()], {
    description: 'The body of the HTTP response returned by the endpoint.',
  }),
  latency_ms: Type.Union([Type.Integer(), Type.Null()], {
    description: 'The time between the event occurrence and this delivery attempt, in milliseconds.',
  }),
  queue_latency_ms: Type.Union([Type.Integer(), Type.Null()], {
    description: 'The time between the job being queued and picked up by the webhook worker, in milliseconds.',
  }),
  response_time_ms: Type.Union([Type.Integer(), Type.Null()], {
    description: 'The response time of the webhook endpoint for this delivery attempt, in milliseconds.',
  }),
});

export const InsertWebhookConversationSchema = createInsertSchema(webhookConversation);

export const PublicWebhookConversationSchema = Type.Composite([
  Type.Omit(WebhookConversationSchema, [
    'id',
    'endpoint_id',
    'event_id',
    'public_id',
    'endpoint_public_id',
    'event_public_id',
    'queue_latency_ms',
  ]),
  Type.Object({
    object: Type.Literal('WebhookConversation', { description: 'The type of the returned object.' }),
    id: TypeIdSchema('webhookConversation', { description: 'Unique identifier of the webhook conversation.' }),
    endpoint_id: TypeIdSchema('webhookEndpoint', {
      description: 'Unique identifier of the webhook endpoint the event was delivered to.',
    }),
    event_id: TypeIdSchema('event', { description: 'Unique identifier of the event that was delivered.' }),
  }),
]);

export type WebhookConversation = typeof webhookConversation.$inferSelect;
export type InsertWebhookConversation = typeof webhookConversation.$inferInsert;
export type PublicWebhookConversation = Static<typeof PublicWebhookConversationSchema>;
